/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import type { PassImageRequest } from './types';
import { getImageSourceConfig } from './config';
import { resolvePassImagePath, validatePassImageRequest } from './resolver';

/**
 * Get a pass image URL based on the pass type, variant, and options
 *
 * @param request - The image request parameters
 * @returns The full URL to the image
 *
 * @example
 * // Get a medium-sized annual pass for 2025
 * // Results in: annual/2025/annual_md.webp
 * const imageUrl = getPassImage({
 *   passType: 'annual',
 *   variant: { type: 'sized', size: 'md' },
 *   year: '2025'
 * });
 *
 * @example
 * // Get a custom variant for generic passes
 * // Results in: generic/generic_front-medium.webp
 * const frontImage = getPassImage({
 *   passType: 'generic',
 *   variant: { type: 'custom', name: 'front-medium' }
 * });
 *
 * @example
 * // Get a versioned pass with fallback support
 * // Results in: senior-lifetime/v2/senior_lifetime_sm.webp (or fallback paths)
 * const image = getPassImage({
 *   passType: 'senior-lifetime',
 *   variant: { type: 'sized', size: 'sm' },
 *   version: 'v2' // Falls back to v1 if v2 doesn't exist
 * });
 */
export function getPassImage(request: PassImageRequest): string {
    validatePassImageRequest(request);
    const sourceConfig = getImageSourceConfig();
    const possiblePaths = resolvePassImagePath(request);
    const relativePath = possiblePaths[0];

    return `${sourceConfig.baseUrl}/${relativePath}`;
}

// Re-export types for consumers
export type { PassImageRequest, PassImageVariant } from './types';
export { passImageConfig } from './config';
